import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotification } from '../../../../contexts/NotificationContext';
import { DRAFT_KEYS, loadDraft, hasDraft, clearDraft, saveDraft } from '../../../../utils/draftUtils';
import {
  fetchPatientData,
  fetchSurgicalSafetyData,
  fetchSurgicalSafetyNextIp,
  saveSurgicalSafety,
  updateSurgicalSafety,
  generateSurgicalShareToken
} from '../api/surgicalSafetyApi';
import { API_BASE_URL } from '../../../../lib/constants';
import { mapBackendToForm, mapFormToBackend } from '../utils/mapping';
import { useSurgicalSafetyValidation } from './useSurgicalSafetyValidation';

export const useSurgicalSafetyChecklist = (uhid_no, navigate) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  const signaturePadRef = useRef(null);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [currentSignatureRole, setCurrentSignatureRole] = useState("");

  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitStatus, setSubmitStatus] = useState({ open: false, message: "", severity: "success" });

  const [formData, setFormData] = useState({
    identity_confirmed: false,
    site_confirmed: false,
    procedure_confirmed: false,
    consent_confirmed: false,
    site_marked: false,
    anesthesia_safety_check: false,
    pulse_oximeter_functioning: false,
    known_allergy: false,
    difficult_airway: false,
    difficult_airway_assistance_available: false,
    risk_of_500ml_blood_loss: false,
    adequate_iv_fluids_planned: false,
    blood_standby: false,
    serology_status_negative: false,
    serology_status_positive: false,
    ot_nurse_name: '',
    ot_nurse_signature: '',
    team_introduction: false,
    patient_confirmation: false,
    site_confirmation: false,
    procedure_confirmation: false,
    anticipated_critical_event: false,
    anesthesia_team_review: false,
    nursing_team_review: false,
    antibiotic_prophylaxis: false,
    essential_imaging_displayed: false,
    anesthesiologist_name: '',
    anesthesiologist_signature: '',
    nurse_verbal_confirmation: false,
    procedure_recorded: false,
    instrument_sponge_needle_count_checked: false,
    specimen_labelled: false,
    equipment_problem: false,
    recovery_concerns: false,
    post_op_instruction_by_surgeon: false,
    post_op_instruction_by_anesthesiologist: false,
    recovery_charting_done: false,
    surgeon_name: '',
    surgeon_signature: '',
    uhid_no: uhid_no || "",
    ip_no: '',
    ward_room_no: '',
    procedure_name: '',
    primary_consultant: '',
    date_time_of_surgery: '',
  });

  const { errors, setErrors, validateField, validateForm } = useSurgicalSafetyValidation(formData);

  // Queries
  const { data: patientInfo, isLoading: isPatientInfoLoading } = useQuery({
    queryKey: ['patient', uhid_no],
    queryFn: () => fetchPatientData(uhid_no),
    enabled: !!uhid_no
  });

  const { data: nextIpData } = useQuery({
    queryKey: ['surgicalNextIp'],
    queryFn: fetchSurgicalSafetyNextIp,
    enabled: !success
  });

  const { data: existingData, isLoading: isExistingDataLoading } = useQuery({
    queryKey: ['surgicalSafety', uhid_no],
    queryFn: () => fetchSurgicalSafetyData(uhid_no),
    enabled: !!uhid_no,
    retry: false
  });

  useEffect(() => {
    if (existingData && Object.keys(existingData).length > 0) {
      setSuccess('success');
      setFormData(prev => ({ ...prev, ...mapBackendToForm(existingData) }));
    }
  }, [existingData]);

  // Effects
  useEffect(() => {
    if (patientInfo) {
      setFormData(prev => ({ ...prev, name: patientInfo.name }));
    }
  }, [patientInfo]);

  useEffect(() => {
    if (nextIpData && !success && nextIpData.next_ip_no) {
      setFormData(prev => ({ ...prev, ip_no: nextIpData.next_ip_no }));
    }
  }, [nextIpData, success]);

  // Draft loading and saving
  useEffect(() => {
    if (uhid_no && hasDraft(DRAFT_KEYS.SURGICAL_SAFETY_CHECKLIST, uhid_no)) {
      const saved = loadDraft(DRAFT_KEYS.SURGICAL_SAFETY_CHECKLIST, uhid_no);
      if (saved && Object.keys(saved).length > 0 && typeof saved.formData === 'object') {
        setFormData(saved.formData);
      }
    }
  }, [uhid_no]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (uhid_no) {
        saveDraft(DRAFT_KEYS.SURGICAL_SAFETY_CHECKLIST, uhid_no, formData);
      }
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [formData, uhid_no]);

  // Handlers
  const handleChange = useCallback((event) => {
    const { name, type, checked, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  }, [errors, setErrors]);

  const handleCheckboxChange = useCallback((event, value, field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleBlur = useCallback((event) => {
    const { name, value } = event.target;
    validateField(name, value);
  }, [validateField]);

  // Signatures
  const handleSignatureOpen = useCallback((role) => {
    setCurrentSignatureRole(role);
    setSignatureDialogOpen(true);
  }, []);

  const handleSignatureClear = useCallback(() => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  }, []);

  const handleSignatureClose = useCallback(() => {
    setSignatureDialogOpen(false);
  }, []);

  const handleSignatureSave = useCallback(() => {
    const dataURL = signaturePadRef.current.toDataURL();
    setFormData((prevData) => ({
      ...prevData,
      [currentSignatureRole]: dataURL.split(',')[1],
    }));
    setSignatureDialogOpen(false);
  }, [currentSignatureRole]);

  // Form submission mutation
  const mutation = useMutation({
    mutationFn: (data) => {
      if (success === "success") {
        return updateSurgicalSafety(uhid_no, data);
      } else {
        return saveSurgicalSafety(data);
      }
    },
    onSuccess: () => {
      showNotification('success', success === 'success' ? 'Surgical Safety Checklist updated successfully!' : 'Surgical Safety Checklist added successfully!');
      queryClient.invalidateQueries(['surgicalSafety', uhid_no]);
      setSuccess('success');
      clearDraft(DRAFT_KEYS.SURGICAL_SAFETY_CHECKLIST, uhid_no);
      setTimeout(() => {
        navigate(`/mainPage/${uhid_no}`, {
          state: { from: 'PreOperativePhase', status: 'success', step: 1 },
        });
      }, 1000);
    },
    onError: (error) => {
      console.error('Submission error:', error);
      showNotification('error', 'Error in submitting Surgical Safety Checklist form!');
    }
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    const payload = mapFormToBackend(formData);
    mutation.mutate(payload);
  };

  const handleShareClick = async () => {
    try {
      const res = await generateSurgicalShareToken(uhid_no);
      const token = res.token;
      const link = `${API_BASE_URL}/surgicalSafetyChecklist/${uhid_no}?token=${token}`;
      const message = `Dear Doctor,\n\nPlease review the Surgical Safety form:\n\n${link}`;

      await navigator.clipboard.writeText(link);
      setSubmitStatus({
        open: true,
        message: "Secure link copied to clipboard!",
        severity: "info",
      });

      const phoneNumber = '+916379605575';
      window.open(`http://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    } catch (error) {
      console.error("Error generating share token:", error);
      setSubmitStatus({
        open: true,
        message: "Failed to generate shareable link",
        severity: "error",
      });
    }
  };

  return {
    formData, setFormData,
    errors, setErrors,
    success, setSuccess,
    loading: isPatientInfoLoading,
    submitStatus,
    handleChange, handleCheckboxChange, handleBlur,
    signaturePadRef, signatureDialogOpen, handleSignatureOpen, handleSignatureClear, handleSignatureClose, handleSignatureSave,
    handleSubmit, handleShareClick,
    mutationLoading: mutation.isLoading
  };
};
